import { useEffect, useRef } from 'react';
import { useCartStore } from '@/stores/cartStore';

/**
 * Hook to sync cart state with Shopify after checkout.
 * Clears local cart if the Shopify cart no longer exists or is empty
 * (indicating checkout was completed).
 * 
 * This hook should be used in the main App/layout component.
 */
export function useCartSync() {
  const syncRegularCart = useCartStore(function(state) { return state.syncCart; });
  const regularHasHydrated = useCartStore(function(state) { return state._hasHydrated; });
  
  // Track if we've done the initial sync
  const hasInitialSyncRef = useRef(false);

  // Sync after store is hydrated (initial load only)
  useEffect(function() {
    if (regularHasHydrated && !hasInitialSyncRef.current) {
      hasInitialSyncRef.current = true;
      console.log('Cart store hydrated, running initial cart sync...');
      syncRegularCart();
    }
  }, [regularHasHydrated, syncRegularCart]);

  // Sync when user returns to the tab (only if hydrated)
  useEffect(function() {
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        const regularStore = useCartStore.getState();
        
        if (regularStore._hasHydrated) {
          console.log('Tab became visible, syncing cart...');
          syncRegularCart();
        } else {
          console.log('Tab visible but store not yet hydrated, skipping sync');
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return function() {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [syncRegularCart]);
}
